import React, { useState, useEffect } from "react";
import ToastContainer from "react-bootstrap/ToastContainer";
import { NotificationPopup } from "./NotificationPopup";
import SanitizeHTML from "./Sanitize";
import { NotificationEvent } from "../notifications/notifications";

const HIDE_NOTIFICATION_DELAY = 5000;

interface NotificationType {
  message: string | React.ReactNode;
  variant: string;
  sticky?: boolean | null;
  html?: boolean | null;
  onClose?: () => void;
  id: string;
}

const allowedHTMLOptions = {
  ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "p", "span"],
  ALLOWED_ATTR: ["href", "onclick", "class"]
};

const NotificationContainer: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationType[]>([]);

  useEffect(() => {
    const handleAddNotification = (evt: CustomEvent) => {
      setNotifications(n => [...n, { ...evt.detail }]);
    };

    const handleDeleteNotification = (evt: CustomEvent) => {
      setNotifications(n =>
        n.filter(notification => notification.id !== evt.detail.id)
      );
    };

    document.addEventListener(NotificationEvent.ADD, handleAddNotification);
    document.addEventListener(
      NotificationEvent.DELETE,
      handleDeleteNotification
    );

    return () => {
      document.removeEventListener(
        NotificationEvent.ADD,
        handleAddNotification
      );
      document.removeEventListener(
        NotificationEvent.DELETE,
        handleDeleteNotification
      );
    };
  }, []);

  const renderMessage = (options: NotificationType) => {
    if (typeof options.message !== "string") {
      return options.message;
    }

    if (options.html) {
      return (
        <SanitizeHTML html={options.message} options={allowedHTMLOptions} />
      );
    }

    return options.message;
  };

  return (
    <>
      <ToastContainer
        className={"notification-container"}
        data-testid="notification-container"
      >
        {notifications.map(options => (
          <NotificationPopup
            variant={options.variant}
            id={options.id}
            key={options.id}
            delay={HIDE_NOTIFICATION_DELAY}
            autohide={!options.sticky}
            onClose={options?.onClose}
          >
            {renderMessage(options)}
          </NotificationPopup>
        ))}
      </ToastContainer>
    </>
  );
};

export default NotificationContainer;
